import * as React from "react";
import { LoadingSpinner } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AgentActivity {
  userId: number;
  displayName: string;
  email: string;
  currentChats: number;
  status: string;
}

interface SiteActivity {
  siteId: number;
  companyName: string;
  activeAgents: AgentActivity[];
  totalAgents: number;
  todayConversations: {
    botOnly: number;
    humanOnly: number;
    botAndHuman: number;
    total: number;
  };
}

interface GlobalStats {
  totalAgentsOnline: number;
  totalSitesActive: number;
  globalConversationsToday: {
    botOnly: number;
    humanOnly: number;
    botAndHuman: number;
    total: number;
  };
}

interface PlatformAlert {
  severity: "error" | "warning" | "info";
  category: string;
  siteId: number;
  title: string;
  detail: string;
  campaignId: number | null;
  ts: string;
}

interface AlertsData {
  generatedAt: string;
  errorCount: number;
  warningCount: number;
  allClear: boolean;
  alerts: PlatformAlert[];
}

// ─── Alerts tab ───────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  error:   { bar: "bg-red-500",    badge: "bg-red-100 text-red-700 border-red-200",   dot: "bg-red-500",    icon: "🔴" },
  warning: { bar: "bg-amber-400",  badge: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-400", icon: "🟡" },
  info:    { bar: "bg-blue-400",   badge: "bg-blue-100 text-blue-700 border-blue-200",  dot: "bg-blue-400",   icon: "🔵" },
};

function AlertsPanel() {
  const api = useApi();
  const [data, setData] = React.useState<AlertsData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [lastUpdate, setLastUpdate] = React.useState<Date>(new Date());
  const [filter, setFilter] = React.useState<"all" | "error" | "warning">("all");

  const load = React.useCallback(async () => {
    try {
      const res = await api.admin.get("LiveMonitor/PlatformAlerts");
      const json = await res.json();
      if (json && Array.isArray(json.alerts)) {
        setData(json);
        setLastUpdate(new Date());
      }
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api]);

  React.useEffect(() => {
    load();
    const iv = setInterval(load, 60_000); // refresh every 60s
    return () => clearInterval(iv);
  }, [load]);

  const filtered = React.useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.alerts;
    return data.alerts.filter(a => a.severity === filter);
  }, [data, filter]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="grid grid-cols-3 gap-4">
        <div
          onClick={() => setFilter(filter === "error" ? "all" : "error")}
          className={`rounded-xl p-4 cursor-pointer border-2 transition-all ${filter === "error" ? "border-red-400 bg-red-50" : "border-transparent bg-white shadow-sm hover:shadow"}`}
        >
          <div className="text-3xl font-bold text-red-600">{data?.errorCount ?? 0}</div>
          <div className="text-sm text-slate-600 mt-1">🔴 Errors</div>
        </div>
        <div
          onClick={() => setFilter(filter === "warning" ? "all" : "warning")}
          className={`rounded-xl p-4 cursor-pointer border-2 transition-all ${filter === "warning" ? "border-amber-400 bg-amber-50" : "border-transparent bg-white shadow-sm hover:shadow"}`}
        >
          <div className="text-3xl font-bold text-amber-600">{data?.warningCount ?? 0}</div>
          <div className="text-sm text-slate-600 mt-1">🟡 Warnings</div>
        </div>
        <div className="rounded-xl p-4 bg-white shadow-sm flex flex-col justify-center">
          <div className={`text-sm font-semibold ${data?.allClear ? "text-green-600" : "text-slate-500"}`}>
            {data?.allClear ? "✅ All clear" : "Issues detected"}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Updated {lastUpdate.toLocaleTimeString()}
          </div>
          <button onClick={load} className="text-xs text-blue-600 underline mt-1 text-left">Refresh now</button>
        </div>
      </div>

      {/* Alert list */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center text-slate-400">
          {data?.allClear
            ? "✅ No issues detected across all sites and services."
            : `No ${filter} alerts.`}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((alert, i) => {
            const s = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.info;
            return (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden flex">
                <div className={`w-1.5 flex-none ${s.bar}`} />
                <div className="flex-1 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${s.badge}`}>
                          {s.icon} {alert.severity.toUpperCase()}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                          {alert.category}
                        </span>
                        {alert.siteId > 0 && (
                          <span className="text-xs text-slate-400">Site {alert.siteId}</span>
                        )}
                        {alert.campaignId && (
                          <span className="text-xs text-slate-400">Campaign #{alert.campaignId}</span>
                        )}
                      </div>
                      <div className="mt-1.5 font-medium text-slate-800 text-sm">{alert.title}</div>
                      <div className="mt-0.5 text-xs text-slate-500 leading-relaxed">{alert.detail}</div>
                    </div>
                    <div className="text-xs text-slate-400 whitespace-nowrap">
                      {new Date(alert.ts).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-xs text-slate-400 text-center pt-2">
        Checks: IVR failure rates · stalled campaigns · high opt-out · email stuck · missing App Settings · auto-refreshes every 60s
      </div>
    </div>
  );
}

// ─── Dashboard tab (existing) ─────────────────────────────────────────────────

function DashboardPanel() {
  const api = useApi();
  const [data, setData] = React.useState<{ sites: SiteActivity[]; global: GlobalStats } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [lastUpdate, setLastUpdate] = React.useState<Date>(new Date());
  const [refreshInterval, setRefreshInterval] = React.useState(10);

  const fetchData = React.useCallback(async () => {
    try {
      const response = await api.admin.get("LiveMonitor/Dashboard");
      const result = await response.json();
      if (result && result.global && Array.isArray(result.sites)) {
        setData(result);
        setLastUpdate(new Date());
      }
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api]);

  React.useEffect(() => { fetchData(); }, [fetchData]);
  React.useEffect(() => {
    const iv = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(iv);
  }, [fetchData, refreshInterval]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  }

  return (
    <>
      <div className="flex justify-end mb-4 items-center gap-2">
        <span className="text-sm text-slate-500">Last updated: {lastUpdate.toLocaleTimeString()}</span>
        <label className="text-sm text-slate-600">Refresh every:</label>
        <select value={refreshInterval} onChange={e => setRefreshInterval(Number(e.target.value))}
          className="px-3 py-1 border border-slate-300 rounded-md text-sm">
          <option value={5}>5s</option>
          <option value={10}>10s</option>
          <option value={30}>30s</option>
          <option value={60}>1m</option>
        </select>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Sites Active",      val: data.global.totalSitesActive,              color: "text-blue-600" },
              { label: "Agents Online",     val: data.global.totalAgentsOnline,              color: "text-green-600" },
              { label: "Total Chats Today", val: data.global.globalConversationsToday.total, color: "text-purple-600" },
              { label: "Avg / Site",        val: data.global.totalSitesActive > 0
                  ? Math.round(data.global.globalConversationsToday.total / data.global.totalSitesActive) : 0, color: "text-orange-600" },
            ].map(({ label, val, color }) => (
              <div key={label} className="bg-white rounded-lg shadow-sm p-5">
                <div className="text-sm text-slate-600 mb-1">{label}</div>
                <div className={`text-3xl font-bold ${color}`}>{val}</div>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {data.sites.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center text-slate-500">
                No active sites at the moment
              </div>
            ) : data.sites.map(site => (
              <div key={site.siteId} className="bg-white rounded-lg shadow-sm p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-semibold text-slate-800">{site.companyName}</div>
                    <div className="text-xs text-slate-500">Site {site.siteId}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{site.totalAgents}</div>
                    <div className="text-xs text-slate-500">Agents Online</div>
                  </div>
                </div>
                {site.activeAgents.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {site.activeAgents.map(agent => (
                      <div key={agent.userId} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            agent.status === "Available" ? "bg-green-500" :
                            agent.status === "Busy" ? "bg-red-500" : "bg-yellow-500"
                          }`} />
                          <div>
                            <div className="text-sm font-medium text-slate-800">{agent.displayName}</div>
                            <div className="text-xs text-slate-500">{agent.email}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">{agent.currentChats}</div>
                          <div className="text-xs text-slate-500">chats</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ─── Synthetic Monitoring tab ─────────────────────────────────────────────────

interface SyntheticProbeResult {
  testName: string;
  status: string;
  durationMs: number;
  errorMessage?: string;
  timestamp: string;
  passRate24h?: number;
  totalRuns24h?: number;
}

interface SyntheticData {
  enabled: boolean;
  circuitBreaker: { isOpen: boolean; consecutiveFailures: number };
  testResults: SyntheticProbeResult[];
}

function SyntheticPanel() {
  const api = useApi();
  const [data, setData] = React.useState<SyntheticData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<any>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [lastUpdate, setLastUpdate] = React.useState(new Date());

  const load = React.useCallback(async () => {
    try {
      const res = await api.messaging.get("Diagnostics/synthetic");
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setLastUpdate(new Date());
      }
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api]);

  const loadDetail = React.useCallback(async (testName: string) => {
    setSelected(testName);
    setDetailLoading(true);
    try {
      const res = await api.messaging.get(`Diagnostics/synthetic/${testName}`);
      if (res.ok) setDetail(await res.json());
    } catch { /* non-fatal */ }
    finally { setDetailLoading(false); }
  }, [api]);

  React.useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  }

  const probes = data?.testResults ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {data?.circuitBreaker?.isOpen && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
              ⚡ Circuit breaker OPEN — {data.circuitBreaker.consecutiveFailures} consecutive failures
            </span>
          )}
          {!data?.circuitBreaker?.isOpen && probes.length > 0 && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200">
              ✅ Circuit breaker closed
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">Updated {lastUpdate.toLocaleTimeString()}</span>
          <button onClick={load} className="text-xs text-blue-600 underline">Refresh</button>
        </div>
      </div>

      {probes.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-slate-400 shadow-sm">
          No synthetic test results. Probes run on a cron schedule.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {probes.map(probe => {
            const pass = probe.status === "Pass";
            return (
              <button
                key={probe.testName}
                type="button"
                onClick={() => loadDetail(probe.testName)}
                className={`text-left rounded-xl border-2 p-4 shadow-sm hover:shadow transition-all ${
                  selected === probe.testName ? "ring-2 ring-blue-400" : ""
                } ${pass ? "bg-white border-green-200" : "bg-red-50 border-red-300"}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="font-semibold text-slate-800 text-sm">{probe.testName}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    pass ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>{pass ? "✓ Pass" : "✗ Fail"}</span>
                </div>
                <div className="text-xs text-slate-500 space-y-1">
                  <div>{probe.durationMs}ms</div>
                  {probe.passRate24h !== undefined && (
                    <div className={probe.passRate24h < 90 ? "text-red-600 font-medium" : ""}>
                      24h: {(probe.passRate24h * 100).toFixed(1)}% ({probe.totalRuns24h} runs)
                    </div>
                  )}
                  {!pass && probe.errorMessage && (
                    <div className="text-red-600 text-xs truncate" title={probe.errorMessage}>
                      {probe.errorMessage}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
            <span className="font-semibold text-slate-800">{selected} — last 50 runs</span>
            <button onClick={() => { setSelected(null); setDetail(null); }} className="text-slate-400 hover:text-slate-700 text-lg leading-none">✕</button>
          </div>
          {detailLoading ? (
            <div className="flex justify-center py-8"><LoadingSpinner /></div>
          ) : detail ? (
            <div className="overflow-y-auto max-h-80">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-2 text-left text-slate-500 font-medium">Status</th>
                    <th className="px-4 py-2 text-left text-slate-500 font-medium">Duration</th>
                    <th className="px-4 py-2 text-left text-slate-500 font-medium">Time</th>
                    <th className="px-4 py-2 text-left text-slate-500 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {(detail.recentResults ?? []).map((r: any, i: number) => (
                    <tr key={i} className={r.status === "Pass" ? "" : "bg-red-50"}>
                      <td className={`px-4 py-2 font-semibold ${r.status === "Pass" ? "text-green-600" : "text-red-600"}`}>
                        {r.status === "Pass" ? "✓" : "✗"} {r.status}
                      </td>
                      <td className="px-4 py-2 text-slate-500">{r.durationMs}ms</td>
                      <td className="px-4 py-2 text-slate-400 whitespace-nowrap">
                        {new Date(r.timestamp).toLocaleTimeString()}
                      </td>
                      <td className="px-4 py-2 text-red-600 max-w-xs truncate" title={r.errorMessage ?? ""}>
                        {r.errorMessage ?? ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}

      <div className="text-xs text-slate-400 text-center pt-2">
        Probes: ApiHealth · ContactsLoad · InboxLoad · ConversationE2E · DatabaseWriteRead — auto-refreshes every 60s
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function LiveMonitor() {
  const [tab, setTab] = React.useState<"alerts" | "dashboard" | "synthetic">("alerts");

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Live Monitor</h1>
          <p className="text-slate-500 text-sm mt-0.5">Velaro platform health — proactive alerts + real-time agent activity</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-full shadow px-3 py-1.5">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-xs text-slate-600 font-medium">Live</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-lg shadow-sm p-1 w-fit">
        {([
          { id: "alerts",    label: "🚨 Platform Alerts" },
          { id: "synthetic", label: "🔬 Synthetic Monitoring" },
          { id: "dashboard", label: "📊 Agent Dashboard" },
        ] as const).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.id ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "alerts"    && <AlertsPanel />}
      {tab === "synthetic" && <SyntheticPanel />}
      {tab === "dashboard" && <DashboardPanel />}
    </div>
  );
}
